// components/Footer.jsx
import React, { useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useEnquiryModal } from "./EnquiryModal";
import { useLeadForm } from "../hooks/useLeadForm";
import { Link, useNavigate } from "react-router-dom";

const Footer = () => {
  const { openModal } = useEnquiryModal();
  const navigate = useNavigate();   // initialize navigate

  const {
    phone,
    setPhone,
    dialCode,
    isoCode,
    loading,
    success,
    setSuccess,
    submitLead,
    setDialCode,
    setIsoCode,
    setNumberWithoutCountryCode
  } = useLeadForm();

  const [consentChecked, setConsentChecked] = useState(true);
  const [errors, setErrors] = useState({ name: "", phone: "", email: "", message: "" });

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const name = e.target.name.value.trim();
    const email = e.target.email.value.trim();
    const message = e.target.message.value.trim();
    const cleanedPhone = phone ? phone.replace(/\D/g, "") : "";

    const newErrors = { name: "", phone: "", email: "", message: "" };

    // ✅ Name validation
    if (!name) {
      newErrors.name = "Name is required";
    }
    // ✅ Email validation
    if (!email) {
      newErrors.email = "Email is required";
    }

    // ✅ Phone validation (strong)
    if (!cleanedPhone) {
      newErrors.phone = "Phone number is required";
    }
    // Allow country code but ensure at least 8 digits total (including code)
    else if (cleanedPhone.length < 8) {
      newErrors.phone = "Please enter a valid phone number";
    }

    // ✅ Message validation      
    if (!message) {
      newErrors.message = "Message is required";
    }
    // Stop form if errors exist
    if (newErrors.name || newErrors.phone || newErrors.email || newErrors.message) {
      setErrors(newErrors);
      return;
    }

    // ✅ No errors — clear errors and submit
    setErrors({ name: "", phone: "", email: "", message: "" });
    const formData = { name, email, phone: cleanedPhone, message };

    const result = await submitLead(formData, e?.target);
    if (result) {
      e.target.reset();
      setConsentChecked(false);
      navigate("/thankyou.html");
    }
  };

  return (
    <div>
      <footer id="contact" className="w-full bg-blue-50 p-4 sm:p-8 lg:p-20">
        <div className="container mx-auto">
          {/* Desktop Footer */}
          <div className="hidden lg:flex border-2 rounded border-sky-600 p-16 flex-col items-center">
            <h2 className="text-black text-2xl font-bold mb-4 ">ENQUIRE NOW</h2>
            <form
              onSubmit={handleFormSubmit}
              className="flex items-end justify-center space-x-4 w-full max-w-5xl"
            >
              {/* Name input */}
              <div className="flex-1 flex flex-col relative">
                <label className="text-black mb-1 text-sm font-semibold">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter your name"
                  className="h-[48px] p-3 rounded-lg bg-white border-2 border-sky-600 focus:border-sky-600 outline-none w-full"
                  required
                />
                {errors.name && (
                  <span className="absolute bottom-[-18px] text-black text-sm mt-1">{errors.name}</span>
                )}
              </div>

              {/* Phone input */}
              <div className="flex-1 flex flex-col relative">
                <label className="text-black mb-1 text-sm font-semibold">
                  Phone Number
                </label>
                <PhoneInput
                  country={isoCode || "in"}
                  value={phone}
                  onChange={(value, country) => {
                    setPhone(value);
                    setDialCode(`${country.dialCode}`);
                    setIsoCode(country.countryCode);
                    setNumberWithoutCountryCode(value.replace(country.dialCode, ""));
                  }}
                  inputProps={{ name: "phone", required: true }}
                  containerClass="w-full"
                  inputClass="!w-full !h-[48px] !pl-12 !pr-3 !rounded-lg !bg-white !text-black !border-2 !border-sky-600 focus:!border-sky-600 !outline-none placeholder:!text-gray-400"
                  buttonClass="!h-[48px] !flex !items-center !justify-center !bg-gray-300 !border-none !rounded-l-lg "
                  dropdownClass="!bg-white !text-black"
                  searchClass="!bg-white !text-black"
                  enableSearch={true}
                  placeholder="Enter phone number"
                  required
                />
                {errors.phone && (
                  <span className="absolute bottom-[-18px] text-black text-sm mt-1">{errors.phone}</span>
                )}
              </div>

              <div className="flex-1 flex flex-col">
                <label className="text-black mb-1 text-sm font-semibold">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  className="h-[48px] p-3 rounded-lg bg-white border-2 border-sky-600 focus:border-sky-600 outline-none w-full"
                  required
                />
                {errors.email && (
                  <span className="absolute bottom-[-18px] text-black text-sm mt-1">{errors.email}</span>
                )}
              </div>

              <div className="flex-1 flex flex-col">
                <label className="text-black mb-1 text-sm font-semibold">
                  Message
                </label>
                <input
                  type="text"
                  name="message"
                  placeholder="Enter your message"

                  className="h-[48px] p-3 rounded-lg bg-white border-2 border-sky-600 focus:border-sky-600 outline-none w-full"
                  required
                />
                {errors.message && (
                  <span className="absolute bottom-[-18px] text-black text-sm mt-1">{errors.message}</span>
                )}
              </div>

              <button
                type="submit"
                className="bg-sky-600 text-[#ffffff] px-8 py-2.5 rounded-lg font-bold hover:text-sky-600 border-2 border-sky-600 hover:bg-white transition-colors h-fit"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Enquire"}
              </button>
            </form>
          </div>

          {/* Mobile/Tablet Footer */}
          <div className="lg:hidden mb-5 flex flex-col items-center space-y-4">
            <h2 className="text-black mt-5 text-2xl font-bold">ENQUIRE NOW</h2>
            <form onSubmit={handleFormSubmit} className="w-full space-y-4">
              <div className="flex flex-col">
                <label className="text-black mb-1 text-sm font-semibold">Name</label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter your name"
                  className="h-[48px] w-full p-3 rounded-lg bg-white border-2 border-sky-600 focus:border-sky-600 outline-none"
                  required
                />
                {errors.name && (
                  <span className="text-black text-sm mt-1">{errors.name}</span>
                )}
              </div>

              <div className="flex flex-col">
                <label className="text-black mb-1 text-sm font-semibold">Phone Number</label>
                <PhoneInput
                  country={isoCode || "in"}
                  value={phone}
                  onChange={(value, country) => {
                    setPhone(value);
                    setDialCode(`${country.dialCode}`);
                    setIsoCode(country.countryCode);
                    setNumberWithoutCountryCode(value.replace(country.dialCode, ""));
                  }}
                  inputProps={{ name: "phone", required: true }}
                  containerClass="w-full "   // ✅ relative so dropdown positions properly
                  inputClass="!w-full !bg-white !text-black !py-3 !pl-12 !pr-3 !rounded-md !border-2 !border-sky-600 focus:!border-sky-600 !outline-none"
                  buttonClass="!bg-gray-800 !border-none !rounded-l-md !bg-gray-200"
                  dropdownClass="!bg-white !text-black "   // ✅ keep dropdown above !z-[9999] !absolute
                  searchClass="!bg-white !text-black"
                  enableSearch={true}
                  required
                />
                {errors.phone && (
                  <span className="text-black text-sm mt-1">{errors.phone}</span>
                )}
              </div>

              <div className="flex flex-col">
                <label className="text-black mb-1 text-sm font-semibold">Email</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  className="h-[48px] w-full p-3 rounded-lg bg-white border-2 border-sky-600 focus:border-sky-600 outline-none"
                  required
                />
                {errors.email && (
                  <span className="text-black text-sm mt-1">{errors.email}</span>
                )}
              </div>

              <div className="flex flex-col">
                <label className="text-black mb-1 text-sm font-semibold">Message</label>
                <input
                  type="text"
                  name="message"
                  placeholder="Enter your message"
                  className="h-[48px] w-full p-3 rounded-lg bg-white border-2 border-sky-600 focus:border-sky-600 outline-none"
                  required
                />
                {errors.message && (
                  <span className="text-black text-sm mt-1">{errors.message}</span>
                )}
              </div>

              <button
                type="submit"
                className="bg-sky-600 w-full text-lg text-[#ffffff] border-2 border-sky-600 px-8 py-2.5 rounded-lg font-bold hover:text-sky-600 hover:bg-white transition-colors h-fit"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Enquire"}
              </button>
            </form>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Footer;
